// Add this debounce function at the beginning of your file
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Centralized locale string access helper
// Locale strings are now loaded in layout/theme.liquid via window.theme.strings

const getLocaleString = (key, replacements = {}) => {
  let string;
  
  // Get from centralized theme strings
  if (window.theme?.strings?.[key]) {
    string = window.theme.strings[key];
  } else {
    // Fallback to key if string not found
    console.warn(`Locale string not found: ${key}`);
    string = key;
  }
  
  // Replace placeholders with actual values
  Object.keys(replacements).forEach(placeholder => {
    string = string.replace(`{{ ${placeholder} }}`, replacements[placeholder]);
  });
  
  return string;
};

// Every repaint of a line price goes through here. The auto-added gift shows a
// word instead of an amount. Three separate call sites used to write textContent
// directly, so any one of them could put a price back onto the gift line.
//
// Both checks are needed, not one: the payload check covers the render path,
// which writes the price BEFORE it stamps the gift class onto the element, and
// the DOM check covers the quantity paths, which only ever receive a key.
const setCartItemPrice = (priceElement, amountCents, item) => {
  if (!priceElement) return;
  const isGift = Boolean(item?.properties?._auto_gift)
    || Boolean(priceElement.closest('.cart-item')?.classList.contains('cart-item--auto-gift'));

  if (isGift) {
    priceElement.textContent = getLocaleString('cart.free_gift_price');
    return;
  }

  // The line shows its undiscounted price, the way the checkout does. A cart
  // discount is not spread across the lines here; it is stated once beneath the
  // subtotal, so the two screens tell the same story in the same shape.
  //
  // original_line_price is that undiscounted figure. It is absent from the
  // optimistic repaint in updateItemPrice, which passes no item at all, and the
  // amount it computed is already the list price — so falling back to
  // amountCents is right in both cases.
  const original = item?.original_line_price;
  const shown = typeof original === 'number' ? original : amountCents;
  priceElement.textContent = Shopify.formatMoney(shown);
};

// Subtotal, discount rows and total, from one place.
//
// cart.total_price is already discounted, so the subtotal above it is
// reconstructed by adding the discount back — the same arithmetic the Liquid in
// cart.liquid and cart-drawer.liquid does for the server-rendered state, and it
// has to stay in step with it or the figures would change on the first quantity
// update.
//
// cart_level_discount_applications only: line-level discounts are already inside
// each line_price and counting them here would subtract them twice.
const renderCartTotals = (cart) => {
  const applications = cart.cart_level_discount_applications || [];
  const discountTotal = applications.reduce(
    (sum, d) => sum + (d.total_allocated_amount || 0), 0
  );

  // Drawer block only. The cart page's totals are part of the section it gets
  // back from the server, already computed by the Liquid.
  document.querySelectorAll('#menu-cart-discounts').forEach(block => {
    const subtotalLine = block.querySelector('[data-cart-subtotal-line]');
    const subtotalValue = block.querySelector('[data-cart-subtotal]');
    const totalLine = block.querySelector('[data-cart-total-line]');

    // Rows the server did not render cannot be filled in here: the discount rows
    // only exist in the markup when the cart had a discount at page load. A cart
    // that gains its first discount mid-session is left to the next page load
    // rather than being assembled in JS, which would duplicate the Liquid.
    if (subtotalLine && subtotalValue) {
      subtotalLine.style.display = discountTotal > 0 ? '' : 'none';
      subtotalValue.textContent = Shopify.formatMoney(cart.total_price + discountTotal);
    }

    block.querySelectorAll('[data-cart-discount-line]').forEach((line, index) => {
      const application = applications[index];
      if (!application) { line.style.display = 'none'; return; }
      line.style.display = '';
      const amount = line.querySelector('span');
      if (amount) {
        amount.textContent = '\u2212' + Shopify.formatMoney(application.total_allocated_amount);
      }
    });

    // The label does not change with the discount. Subtotal is what the cart's
    // own figure is called either way — shipping and tax still follow, so it is
    // never a total. Only the row's layout class switches, because with rows
    // above it the line has to lay itself out like them.
    if (totalLine) {
      totalLine.classList.toggle('cart-total-line', discountTotal > 0);
    }
  });
};

// Helper function to get template content
const getTemplateContent = (templateId) => {
  const template = document.getElementById(templateId);
  if (!template) {
    console.error(`Template with id "${templateId}" not found`);
    return null;
  }
  return template.content.cloneNode(true);
};

const elements = {
  cartButton: document.getElementById('cart-button'), // Back to single cart button
  closeCartButton: document.getElementById('close-cart-menu'),
  filtersButton: document.getElementById('filters-button'),
  closeFiltersButton: document.getElementById('close-filters-drawer'),
  cartMenu: document.getElementById('cart-menu'),
  countryDrawer: document.getElementById('country-drawer'),
  filtersDrawer: document.getElementById('filters-drawer'),
  overlay: document.getElementById('overlay'),
  desktopMenuOverlay: document.getElementById('desktop-menu-overlay'),
  addToCartForm: document.getElementById('add-to-cart-form'),
  sizeSelect: document.getElementById('size-select'),
  addToCartButton: document.getElementById('add-to-cart-btn'),
  colorOptions: document.querySelector('.color-options'),
  logo: document.getElementById('logo'),
};

let productSplide;
let lastValidImageIndex = 0;

// Reusable functions
const toggleMenu = (menuElement, open) => {
  if (!menuElement) return;
  
  // Handle cart, country drawer and filters (slide-in drawers)
  if (menuElement === elements.cartMenu || menuElement === elements.countryDrawer) {
    menuElement.classList.toggle('active', open);
  } else {
    // For filters drawer (if it exists)
    const translateClass = '-translate-x-full';
    menuElement.classList.toggle(translateClass, !open);
  }
  
  // Only manage overlay if it exists and no other menus are open
  if (elements.overlay) {
    const anyMenuOpen = open ||
      (elements.cartMenu && elements.cartMenu.classList.contains('active')) ||
      (elements.countryDrawer && elements.countryDrawer.classList.contains('active')) ||
      (elements.filtersDrawer && !elements.filtersDrawer.classList.contains('-translate-x-full'));
    
    // Overlay has fixed z-50 - no dynamic switching needed
    
    elements.overlay.classList.toggle('opacity-0', !anyMenuOpen);
    elements.overlay.classList.toggle('pointer-events-none', !anyMenuOpen);
  }
  
  // Disable body scrolling when any drawer is open
  const anyDrawerOpen =
    (elements.cartMenu && elements.cartMenu.classList.contains('active')) ||
    (elements.countryDrawer && elements.countryDrawer.classList.contains('active')) ||
    (elements.filtersDrawer && !elements.filtersDrawer.classList.contains('-translate-x-full'));
  
  if (anyDrawerOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const fetchCartData = () => fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
  cache: 'no-store'
}).then(response => {
  if (!response.ok) throw new Error('Cart request failed: ' + response.status);
  return response.json();
});

const closeAllMenus = () => {
  console.log('Closing all menus');
  if (elements.cartMenu) toggleMenu(elements.cartMenu, false);
  if (elements.countryDrawer) toggleMenu(elements.countryDrawer, false);
  if (elements.filtersDrawer) toggleMenu(elements.filtersDrawer, false);
  // Close desktop menu if it exists
  if (window.desktopMenu && window.desktopMenu.isOpen) {
    window.desktopMenu.hideMenu();
  }
  // Close mobile menu if it exists
  if (window.mobileMenu && window.mobileMenu.isOpen()) {
    window.mobileMenu.close();
  }
};



const updateCart = async (cart) => {
  console.log('🛒 updateCart called with', cart.items.length, 'items');
  
  // Update cart UI components immediately, preorder message in parallel
  updateMenuCart(cart);
  updateMainCart(cart);
  // After both, because updateMainCart writes #cart-total itself and this has to
  // be the last word on the totals block.
  renderCartTotals(cart);

  if (window.CartPreorderMessage) {
    window.CartPreorderMessage.updateMessage(cart).catch(error => {
      console.error('Error updating preorder message:', error);
    });
  }
  updateCartCount(cart.item_count);
  
  // Update free shipping progress if function exists
  if (window.updateFreeShippingProgress) {
    window.updateFreeShippingProgress(cart);
  }

  // Update + sync free gift addon if function exists
  if (window.updateFreeGiftProgress) {
    window.updateFreeGiftProgress(cart);
  }
  if (window.syncFreeGiftAddon) {
    window.syncFreeGiftAddon(cart);
  }
};

const updateCartContents = async () => {
  try {
    const cart = await fetchCartData();
    await updateCart(cart);
  } catch (error) {
    console.error('Error updating cart contents:', error);
  }
};

const debouncedUpdateCartContents = debounce(updateCartContents, 300);

const updateCartItemElement = (cartItem, item, prefix) => {
  const cartItemElement = cartItem.querySelector('.cart-item');
  cartItemElement.dataset.key = item.key;
  // Two unit prices, because two different sums are estimated from them.
  // unitPrice is pre-discount and matches what the line displays, so the
  // optimistic repaint lands where the server's original_line_price will.
  // unitPriceNet is what is actually charged and feeds the subtotal and the
  // free-shipping and free-gift thresholds, which all work on the discounted
  // figure. Using the discounted one for the line made the price flicker on
  // every click; using the pre-discount one for the totals would overstate them.
  cartItemElement.dataset.unitPrice = item.original_price;
  cartItemElement.dataset.unitPriceNet = item.price;
  
  const img = cartItem.querySelector('img');
  img.src = item.image;
  img.alt = item.title;
  
  const titleLink = cartItem.querySelector('.cart-item-title-link');
  titleLink.href = item.url;
  titleLink.textContent = item.product_title;
  
  const variantTitle = cartItem.querySelector('.cart-item-variant');
  if (item.variant_title && item.variant_title !== 'Default Title') {
    variantTitle.textContent = item.variant_title;
  } else {
    variantTitle.remove();
  }
  
  // Handle preorder badge
  const preorderBadge = cartItem.querySelector('.cart-item-preorder-badge');
  if (preorderBadge) {
    // Extract handle from item URL
    const handle = item.handle || (item.url ? item.url.match(/\/products\/([^\/\?]+)/)?.[1] : null);
    if (handle) {
      // Fetch product data to check for preorder tag
      fetch(`/products/${handle}.js`)
        .then(response => response.json())
        .then(productData => {
          if (productData.tags) {
            const tags = Array.isArray(productData.tags) 
              ? productData.tags 
              : productData.tags.split(',').map(t => t.trim());
            
            if (tags.includes('preorder') || tags.includes('preorder2') || tags.includes('preorder ohne bestandsartikel') || tags.includes('preorder mit bestandsartikel')) {
              preorderBadge.classList.remove('hidden');
              preorderBadge.classList.add('block');
            }
          }
        })
        .catch(error => console.error('Error fetching product tags:', error));
    }
  }
  
  const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
  const inputId = `${prefix}quantity-${item.key}`;
  quantityInput.value = item.quantity;
  quantityInput.dataset.key = item.key;
  quantityInput.dataset.currentQuantity = item.quantity;
  quantityInput.id = inputId;
  
  // Update label's for attribute
  const label = cartItem.querySelector('label');
  if (label) {
    label.setAttribute('for', inputId);
  }
  
  // Update quantity button states
  const wrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
  if (wrapper) {
    updateQuantityButtonStates(wrapper);
  }
  
  const price = cartItem.querySelector('.cart-item-price');
  setCartItemPrice(price, item.line_price, item);

  // Update remove button data attributes
  const removeForm = cartItem.querySelector('.cart-item-remove form');
  const removeInput = removeForm?.querySelector('input[name="id"]');
  const removeButton = cartItem.querySelector('[data-cart-remove]');

  if (removeInput) {
    removeInput.value = item.key;
  }

  if (removeButton) {
    removeButton.dataset.itemTitle = item.product_title;
    removeButton.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
  }

  // Auto-added free gift: no manual remove button, and a stepper whose plus is dead.
  if (item.properties && item.properties._auto_gift) {
    const removeContainer = cartItem.querySelector('.cart-item-remove');
    const quantityWrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
    if (removeContainer) removeContainer.style.display = 'none';
    if (quantityWrapper) {
      // The stepper stays on screen for the gift, it is no longer hidden. Only
      // the plus is dead: one gift is all there is. The minus is how a customer
      // who does not want it gets rid of it.
      quantityWrapper.style.display = '';
      const increaseButton = quantityWrapper.querySelector('.cart-item-quantity-increase');
      if (increaseButton) {
        increaseButton.disabled = true;
        increaseButton.setAttribute('aria-disabled', 'true');
      }
    }
    cartItemElement.classList.add('cart-item--auto-gift');
  }
};

const updateQuantityButtonStates = (wrapper) => {
  const input = wrapper.querySelector('.cart-item-quantity-input');
  const decreaseButton = wrapper.querySelector('.cart-item-quantity-decrease');
  const quantity = parseInt(input.value);
  
  // Deliberately never disabled. Minus at quantity 1 is now the remove action
  // that replaced the trash icon, so greying it out there would take the only
  // way to remove an item away with it.
  if (decreaseButton) {
    decreaseButton.disabled = false;
    decreaseButton.classList.remove('opacity-30', 'cursor-not-allowed');
  }
};

// Section Rendering state. Declared up here because updateTotalPriceEstimate and
// the batched write both reach for it, and both sit above the swap itself.
let cartSectionRequest = 0;
let cartSectionDeferred = false;

// The section's id is found in the page, never assumed. Shopify names a section
// instance after the template it belongs to — on this shop it renders as
// shopify-section-template--28690626478406__cart — and that number changes with
// the theme copy, so anything hard-coded works until the theme is duplicated or
// published and then silently stops matching.
//
// form.cart-form is the anchor: it is the outermost element of cart.liquid,
// written once in the whole theme, and it sits outside the {% if cart.items %}
// so it is there for an empty cart too. The drawer has no form at all and is
// rendered after content_for_layout, so nothing else can be picked up here.
const cartSectionElement = () => {
  const form = document.querySelector('form.cart-form');
  return form ? form.closest('[id^="shopify-section-"]') : null;
};

const cartSectionId = () => {
  const element = cartSectionElement();
  return element ? element.id.replace('shopify-section-', '') : null;
};

// Quantity clicks on the cart page are batched.
//
// Each click used to post on its own, and the page is redrawn from the server
// after every write, so five quick clicks meant five writes racing five redraws.
// Now the input moves at once and the write goes out once the clicking stops.
//
// The drawer is untouched by this: the batch only takes clicks whose row sits in
// #cart-items-container. A drawer row falls through to the path it has always
// taken.
const CART_PAGE_WRITE_DELAY = 400;
let cartPageWriteTimer = null;
let cartPageWriteInFlight = false;

// True while a click is waiting to be sent or a write is on its way. The section
// swap holds off in that window, or it would paint the cart as it stands now
// over a number the customer has already clicked.
const cartPageWritePending = () => cartPageWriteTimer !== null || cartPageWriteInFlight;

const scheduleCartPageWrite = (input, quantity) => {
  if (cartPageWriteTimer) clearTimeout(cartPageWriteTimer);

  cartPageWriteTimer = setTimeout(async () => {
    cartPageWriteTimer = null;
    cartPageWriteInFlight = true;
    try {
      // Read now, not when the click happened: a swap in between would have
      // replaced the row and with it the key.
      await updateCartItemQuantity(input.dataset.key, quantity);
    } finally {
      cartPageWriteInFlight = false;
      // updateCartItemQuantity ends in updateCart, which calls updateMainCart —
      // so the swap normally arrives on its own. This catches the case where it
      // did not, and a swap that deferred while we were busy is still owed.
      if (cartSectionDeferred) {
        cartSectionDeferred = false;
        renderCartSection();
      }
    }
  }, CART_PAGE_WRITE_DELAY);
};

const handleCartQuantityChange = (event) => {
  const target = event.target;
  const button = target.closest('.cart-item-quantity-decrease, .cart-item-quantity-increase');
  
  if (button) {
    event.preventDefault();
    const wrapper = button.closest('.cart-item-quantity-wrapper');
    const input = wrapper.querySelector('.cart-item-quantity-input');
    const key = input.dataset.key;
    let quantity = parseInt(input.value);

    const onCartPage = Boolean(button.closest('#cart-items-container'));

    // Minus on the last one removes the item instead of bottoming out at 1.
    // Bail out here rather than falling through: the lines below write the input,
    // repaint the price and PUT the quantity to the server, all of which would
    // fight the removal that the dialog is about to confirm.
    if (button.classList.contains('cart-item-quantity-decrease') && quantity <= 1) {
      // A removal while another row's click is still queued would send a key the
      // pending write is about to retire. Waiting it out is a fraction of a
      // second and keeps one write in the air at a time; pulling the queued
      // write forward instead would put two in flight back to back.
      if (onCartPage && cartPageWritePending()) return;

      const row = button.closest('.cart-item');
      const itemTitle = row?.querySelector('.cart-item-title-link')?.textContent.trim();
      if (row?.classList.contains('cart-item--auto-gift')) {
        declineFreeGift(key, itemTitle);
      } else {
        confirmAndRemoveCartItem(key, itemTitle);
      }
      return;
    }

    if (button.classList.contains('cart-item-quantity-decrease')) {
      quantity = quantity - 1;
    } else if (button.classList.contains('cart-item-quantity-increase')) {
      quantity += 1;
    }

    // Cart page: show the number now, send it once the clicking stops. The row
    // is repainted by the server, so no price estimate is written here.
    if (onCartPage) {
      input.value = quantity;
      updateQuantityButtonStates(wrapper);
      scheduleCartPageWrite(input, quantity);
      return;
    }

    input.value = quantity;
    
    // Update button states
    updateQuantityButtonStates(wrapper);
    
    // Update price immediately in both main cart and menu cart
    updateItemPrice(key, quantity);
    
    // Update cart on server and refresh totals
    updateCartItemQuantity(key, quantity);
  }
};

// Single removal path, shared by the minus at quantity 1 and by any remaining
// remove control. Removing is now reachable by pressing minus one more time,
// which is easy to do by accident, so the confirmation stays rather than being
// dropped along with the trash icon.
const confirmAndRemoveCartItem = async (itemKey, itemTitle) => {
  if (!itemKey) {
    console.error('No item key found for removal');
    return;
  }

  const confirmed = await window.ConfirmationDialog.show({
    title: getLocaleString('cart.remove_item'),
    message: getLocaleString('cart.remove_confirmation', { item_title: itemTitle || 'this item' }),
    confirmText: getLocaleString('cart.remove_yes'),
    cancelText: getLocaleString('cart.remove_cancel')
  });

  if (confirmed) {
    updateCartItemQuantity(itemKey, 0);
  }
};

// Declining the free gift has to be RECORDED, not just executed.
// syncFreeGiftAddon puts the gift back whenever the cart qualifies, so a plain
// removal would be undone by the very next cart update and the minus would look
// broken. The decline is stored as a cart attribute rather than in this tab, so
// it survives a reload and is visible to the server-rendered markup too.
const declineFreeGift = async (itemKey, itemTitle) => {
  if (!itemKey) return;

  const confirmed = await window.ConfirmationDialog.show({
    title: getLocaleString('cart.remove_item'),
    message: getLocaleString('cart.remove_confirmation', { item_title: itemTitle || 'this item' }),
    confirmText: getLocaleString('cart.remove_yes'),
    cancelText: getLocaleString('cart.remove_cancel')
  });

  if (!confirmed) return;

  // Recorded BEFORE the removal, deliberately. The other order leaves a window
  // in which the cart has already lost the gift while the sync still believes it
  // is wanted, and it races to add it back.
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { free_gift_declined: 'true' } })
    });
  } catch (error) {
    console.error('Could not record the declined free gift:', error);
  }

  updateCartItemQuantity(itemKey, 0);
};

const handleCartItemRemoval = async (event) => {
  const target = event.target;
  const button = target.closest('[data-cart-remove]');
  
  // Only proceed if this is actually a cart removal button click
  if (!button) {
    return;
  }
  
  console.log('Cart removal handler called', event.target);
  console.log('Remove button found:', button);
  
  event.preventDefault();
  console.log('Preventing default and processing removal');
  
  const itemTitle = button.dataset.itemTitle || 'this item';
  const form = button.closest('form');
  const itemKey = form?.querySelector('input[name="id"]')?.value;
  
  console.log('Item details:', { itemTitle, itemKey });
  
  if (!itemKey) {
    console.error('No item key found for removal');
    return;
  }
  
  await confirmAndRemoveCartItem(itemKey, itemTitle);
};

// Drawer only. The cart page's rows come back from the server with their price
// already right, and repainting them here from a guess would overwrite that.
const updateItemPrice = (key, quantity) => {
  const cartItems = document.querySelectorAll(`#menu-cart-items-container .cart-item[data-key="${key}"]`);
  
  cartItems.forEach(cartItem => {
    const priceElement = cartItem.querySelector('.cart-item-price');
    const unitPrice = parseFloat(cartItem.dataset.unitPrice);
    // Optimistic repaint so the line reacts to the click without waiting for the
    // server. unitPrice is the pre-discount unit price and the line shows the
    // pre-discount line price, so the guess lands exactly where the server's
    // original_line_price will, and nothing flickers.
    if (priceElement && !isNaN(unitPrice)) {
      setCartItemPrice(priceElement, unitPrice * quantity);
    }
    
    const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
    if (quantityInput) {
      quantityInput.value = quantity;
    }
    
    // Update quantity button states
    const wrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
    if (wrapper) {
      updateQuantityButtonStates(wrapper);
    }
  });

  updateTotalPriceEstimate();

  // Both progress bars, from the same estimated total
  updateProgressBarsEstimate();
};

// Estimates the cart page's subtotal between a click and the server's answer.
// Since that page is redrawn from the server, the estimate would only flicker a
// number that is about to be replaced — and it cannot see a batched click that
// has not been sent yet. Left in place for the drawer's callers, which reach it
// through updateItemPrice.
const updateTotalPriceEstimate = () => {
  if (cartSectionElement()) return;
  const cartItems = document.querySelectorAll('#cart-items-container .cart-item');
  let totalPrice = 0;

  cartItems.forEach(item => {
    // The gift has no quantity field at all — it shows a label instead — so this
    // used to throw on .value the moment a gift sat in the cart. It is also
    // skipped rather than merely guarded: dataset.unitPrice carries the variant's
    // list price, so counting it would make the estimate jump by the bottle's
    // full price and then snap back once the real cart came in.
    if (item.classList.contains('cart-item--auto-gift')) return;
    const quantityInput = item.querySelector('.cart-item-quantity-input');
    if (!quantityInput) return;
    // The charged price, not the one on the line: this feeds the total, which is
    // after discount. Falls back to unitPrice for rows rendered before both
    // attributes existed.
    const price = parseFloat(item.dataset.unitPriceNet ?? item.dataset.unitPrice);
    const quantity = parseInt(quantityInput.value);
    if (isNaN(price) || isNaN(quantity)) return;
    totalPrice += price * quantity;
  });

  const cartTotal = document.getElementById('cart-total');
  if (cartTotal) {
    cartTotal.textContent = Shopify.formatMoney(totalPrice);
  }
};

// Drives BOTH cart progress bars optimistically while a quantity is being
// changed, before the real cart comes back from Shopify. It used to update the
// free-shipping bar only, which is why the gift bar sat still until the fetch
// resolved and looked broken next to a bar that moved instantly.
const updateProgressBarsEstimate = () => {
  // Drawer rows only. The bars are drawn in the drawer, and counting both
  // containers added every line twice whenever the drawer was open on /cart. It
  // would also count a cart page click that has not been sent yet.
  const cartItems = document.querySelectorAll('#menu-cart-items-container .cart-item[data-key]');
  let totalPrice = 0;
  let itemCount = 0;

  cartItems.forEach(item => {
    // Gift excluded, and its missing quantity field guarded: it renders a label
    // instead of a stepper, so reading .value off it threw a TypeError and took
    // the whole estimate — both bars and the total — down with it.
    if (item.classList.contains('cart-item--auto-gift')) return;
    const quantityInput = item.querySelector('.cart-item-quantity-input');
    if (!quantityInput) return;
    // Charged price again: both thresholds are measured against cart.total_price,
    // which is after discount, so the estimate has to be too.
    const price = parseFloat(item.dataset.unitPriceNet ?? item.dataset.unitPrice);
    const quantity = parseInt(quantityInput.value);
    if (!isNaN(price) && !isNaN(quantity)) {
      totalPrice += price * quantity;
      itemCount += quantity;
    }
  });

  // Shaped like a real cart because the gift updater reads cart.items. An empty
  // array is honest here: the gift is already excluded from totalPrice above, so
  // the gift updater finds no gift line and subtracts nothing a second time.
  const estimatedCart = {
    total_price: totalPrice,
    item_count: itemCount,
    items: []
  };

  if (window.updateFreeShippingProgress) {
    window.updateFreeShippingProgress(estimatedCart);
  }

  // Progress display only. syncFreeGiftAddon is deliberately NOT called here: it
  // adds and removes the gift line in the real cart, and firing that off an
  // optimistic guess would mutate the cart on a number that may not survive.
  if (window.updateFreeGiftProgress) {
    window.updateFreeGiftProgress(estimatedCart);
  }
};

// The line key a click was read with can be retired before the click is sent.
// Shopify reissues every key on every cart write, and a second click reads the
// DOM while the first write is still in flight — so it carries a key the server
// has already replaced, gets a 400, and the quantity snaps back.
//
// Resolving it against the live cart immediately before the write fixes that.
// The variant half of the key does not change, so it identifies the line.
//
// Exact match first, so the ordinary case never depends on matching by variant.
// Two lines of one variant only occur in a cart an earlier bad write has split,
// and guessing between them would be worse than sending what we were given.
const currentKeyFor = async (staleKey) => {
  try {
    const cart = await fetchCartData();
    if (cart.items.some(item => item.key === staleKey)) return staleKey;

    const variantId = String(staleKey).split(':')[0];
    const matches = cart.items.filter(item => String(item.variant_id) === variantId);
    return matches.length === 1 ? matches[0].key : staleKey;
  } catch (error) {
    console.error('Could not refresh the line key:', error);
    return staleKey;
  }
};

// One cart write at a time, and only the last one repaints.
//
// Every click used to fire its own /cart/change.js and, when that came back,
// re-fetch the whole cart and redraw. Click twice quickly and the first reply
// arrived while the second request was still in flight: its redraw read a cart
// the server had not finished updating, so the quantity climbed to 3 and then
// fell back to 2. The line key makes it worse — Shopify reissues it on every
// write, so the second click was already posting a key the server had retired.
//
// Requests are therefore chained, and the redraw is skipped for every call but
// the last one in a burst. pending counts what is still queued.
let cartWriteChain = Promise.resolve();
let pendingCartWrites = 0;

const updateCartItemQuantity = (key, quantity) => {
  pendingCartWrites += 1;

  cartWriteChain = cartWriteChain
    .then(async () => {
      // Resolved here, not at click time: anything queued ahead of this may have
      // caused Shopify to reissue the key since the button was pressed.
      const currentKey = await currentKeyFor(key);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: currentKey, quantity: quantity })
      });

      // A rejected write used to be read as a cart and carried on with. That is
      // how a stale key turned into a silent no-op: the line kept the number the
      // click had put there while the server never moved.
      //
      // Redrawing after the rejection is the point. Returning without it left the
      // click's optimistic number on screen with nothing behind it, so removing
      // an item or changing a quantity looked like it had simply been ignored.
      // Pulling the cart back in puts the row where the server actually has it.
      if (!response.ok) {
        console.error('Cart update rejected:', response.status, await response.text());
        if (pendingCartWrites === 1) updateCartContents();
        return;
      }

      const cart = await response.json();

      if (quantity === 0) {
        // Remove the item from both main cart and menu cart
        const mainCartItem = document.querySelector(`#cart-items-container .cart-item[data-key="${currentKey}"]`);
        const menuCartItem = document.querySelector(`#menu-cart-items-container .cart-item[data-key="${currentKey}"]`);

        if (mainCartItem) mainCartItem.remove();
        if (menuCartItem) menuCartItem.remove();
      }

      // Only the last write in a burst repaints. An earlier one would paint the
      // cart as it stood before the clicks that followed it.
      //
      // Not awaited: updateCartContents runs syncFreeGiftAddon, which posts its
      // own cart writes when the gift threshold is crossed and then calls
      // updateCartContents again. Waiting for all of that held the next click
      // back for as long as the gift took to settle.
      if (pendingCartWrites === 1) {
        updateCartContents();
      }

      // Check if cart is empty and update UI accordingly
      if (cart.item_count === 0) {
        updateEmptyCartState();
      }
    })
    .catch(error => console.error('Error updating cart:', error))
    .finally(() => {
      pendingCartWrites -= 1;
    });

  return cartWriteChain;
};

const updateEmptyCartState = () => {
  const mainCartContainer = document.getElementById('cart-items-container');
  const menuCartContainer = document.getElementById('menu-cart-items-container');

  if (mainCartContainer) {
    const mainEmptyTemplate = getTemplateContent('cart-empty-main-template');
    if (mainEmptyTemplate) {
      mainCartContainer.innerHTML = '';
      mainCartContainer.appendChild(mainEmptyTemplate);
    }
  }

  if (menuCartContainer) {
    const menuEmptyTemplate = getTemplateContent('cart-empty-menu-template');
    if (menuEmptyTemplate) {
      menuCartContainer.innerHTML = '';
      menuCartContainer.appendChild(menuEmptyTemplate);
    }
  }

  // Hide checkout buttons and totals
  const checkoutButton = document.querySelector('button[name="checkout"]');
  const additionalCheckoutButtons = document.querySelector('.additional-checkout-buttons');
  const cartTotalContainer = document.querySelector('.cart-total-container');
  const cartNoteSection = document.querySelector('.cart-note-section');

  if (checkoutButton) checkoutButton.style.display = 'none';
  if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = 'none';
  if (cartTotalContainer) cartTotalContainer.style.display = 'none';
  if (cartNoteSection) cartNoteSection.style.display = 'none';
};

const selectColor = (button) => {
  // Remove 'selected' class from all color buttons
  document.querySelectorAll('.color-option').forEach(btn => btn.classList.remove('selected'));
  
  // Add 'selected' class to clicked button
  button.classList.add('selected');
  
  // Get selected color
  const selectedColor = button.getAttribute('data-color');
  
  // Update available sizes based on selected color
  updateAvailableSizes(selectedColor);

  // Check button availability
  const addToCartButton = document.getElementById('add-to-cart-btn');
  if (addToCartButton) {
    // Check if we have a valid variant selection now
    const selectedVariantId = window.VariantSelector ? 
      window.VariantSelector.getSelectedVariantId('main-product') : null;
    
    if (!selectedVariantId) {
      // No valid variant, show appropriate message
      const sizeSelector = document.querySelector('[data-form-id="main-product"].variant-selector');
      if (sizeSelector) {
        // Size selection required
        setAddToCartButtonError(addToCartButton);
      } else {
        // No sizes available for this color
        addToCartButton.textContent = getLocaleString('product.sold_out');
        addToCartButton.disabled = true;
      }
    } else {
      // Valid variant available
      resetAddToCartButton(addToCartButton);
    }
  }

  // Find the first available variant with this color and update the image
  const variants = getProductVariants();
  if (variants.length > 0) {
    const selectedVariant = variants.find(variant => variant.options.includes(selectedColor) && variant.available);
    if (selectedVariant) {
      updateProductImage(selectedVariant);
      // Update URL with selected color
      updateURL(selectedColor);
    }
  }
};

// Helper function to get product variants
const getProductVariants = () => {
  const variantsElement = document.getElementById('product-variants');
  if (!variantsElement) return [];
  
  try {
    return JSON.parse(variantsElement.textContent);
  } catch (error) {
    console.error('Error parsing product variants:', error);
    return [];
  }
};

// Helper function to find variant by options
const findVariantByOptions = (color, size) => {
  const variants = getProductVariants();
  
  if (color && size) {
    return variants.find(variant => 
      variant.options.includes(color) && variant.options.includes(size) && variant.available
    );
  } else if (color) {
    return variants.find(variant => 
      variant.options.includes(color) && variant.available
    );
  } else if (size) {
    return variants.find(variant => 
      variant.options.includes(size) && variant.available
    );
  }
  
  return variants.find(variant => variant.available) || variants[0];
};

const addToCart = (event) => {
  event.preventDefault();
  
  console.log('Add to cart function called');
  
  const form = document.getElementById('add-to-cart-form');
  const colorOptions = document.querySelector('.color-options');
  const addToCartButton = document.getElementById('add-to-cart-btn');
  const formId = form?.dataset.formId || 'main-product';
  
  if (!form) {
    console.error('Add to cart form not found');
    return;
  }
  
  console.log('Form elements found:', {
    form: !!form,
    colorOptions: !!colorOptions,
    addToCartButton: !!addToCartButton,
    formId: formId
  });
  
  console.log('Starting add to cart process');

  // Reset button appearance if validations pass
  if (addToCartButton) resetAddToCartButton(addToCartButton);

  // Get variant ID from VariantSelector or hidden input for single variants
  let selectedVariantId = window.VariantSelector ? window.VariantSelector.getSelectedVariantId(formId) : null;
  
  // For single variant products, check for hidden input
  if (!selectedVariantId) {
    const hiddenVariantInput = form.querySelector('.variant-id-input');
    if (hiddenVariantInput && hiddenVariantInput.value) {
      selectedVariantId = hiddenVariantInput.value;
    }
  }
  
  if (!selectedVariantId) {
    console.log('No variant selected');
    if (addToCartButton) setAddToCartButtonError(addToCartButton);
    return;
  }

  console.log('Selected variant ID:', selectedVariantId);

  console.log('🚀 Add to cart initiated');

  // Set loading state
  if (addToCartButton) {
    addToCartButton.dataset.purchaseLabel ||= addToCartButton.textContent.trim();
    addToCartButton.textContent = getLocaleString('cart.adding');
    addToCartButton.disabled = true;
  }

  // Use VariantSelector's addToCart method if available, otherwise fallback to manual approach
  const addToCartPromise = window.VariantSelector ? 
    window.VariantSelector.addToCart(formId, selectedVariantId, 1) :
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('Product added to cart:', data);
      return fetch('/cart.js');
    })
    .then(response => response.json())
    .then(async cart => {
      await updateCart(cart);
      return cart;
    });

  addToCartPromise
    .then(cart => {
      console.log('Cart updated:', cart);

      // Open cart drawer after cart is updated
      toggleMenu(elements.cartMenu, true);

      // Show the menu cart footer and notes section immediately
      const menuCartFooter = document.querySelector('.menu-cart-footer');
      const cartNotesSection = document.querySelector('.cart-notes-section');
      if (menuCartFooter) {
        menuCartFooter.style.display = 'block';
      }
      if (cartNotesSection) {
        cartNotesSection.style.display = 'block';
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert(getLocaleString('cart.add_to_cart_error'));
    })
    .finally(() => {
      // Reset button state
      if (addToCartButton) resetAddToCartButton(addToCartButton);
    });
};

function updateProductImageBasedOnSelection() {
  const sizeSelect = document.getElementById('size-select');
  const colorOptions = document.querySelector('.color-options');
  
  if (!sizeSelect) return;
  
  const selectedSize = sizeSelect.value;
  const selectedColor = document.querySelector('.color-option.selected')?.getAttribute('data-color');
  
  if (!selectedSize && !selectedColor) return;
  
  const selectedVariant = findVariantByOptions(selectedColor, selectedSize);
  if (selectedVariant) {
    updateProductImage(selectedVariant);
  }
}

// Event listeners setup
const setupEventListeners = () => {
  if (elements.cartButton) {
    elements.cartButton.addEventListener('click', async (e) => {
      e.stopPropagation();
      e.preventDefault();
      console.log('Cart button clicked, opening cart drawer');
      
      // Check for preorder message before opening cart
      if (window.CartPreorderMessage) {
        try {
          await window.CartPreorderMessage.checkCart();
        } catch (error) {
          console.error('Error checking preorder message:', error);
        }
      }
      
      toggleMenu(elements.cartMenu, true);
    });
  }
  if (elements.closeCartButton) {
    elements.closeCartButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.cartMenu, false);
    });
  }
  
  // Filter drawer event listeners
  if (elements.filtersButton) {
    elements.filtersButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.filtersDrawer, true);
    });
  }
  if (elements.closeFiltersButton) {
    elements.closeFiltersButton.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleMenu(elements.filtersDrawer, false);
    });
  }
  
  // Only add overlay listener if overlay exists and is the main shared overlay
  if (elements.overlay) {
    elements.overlay.addEventListener('click', closeAllMenus);
  }

  if (elements.desktopMenuOverlay) {
    elements.desktopMenuOverlay.addEventListener('click', () => {
      if (window.desktopMenu && window.desktopMenu.isOpen) {
        window.desktopMenu.hideMenu();
      }
    });
  }

  // ESC key to close menus
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllMenus();
    }
  });

  document.addEventListener('click', handleCartQuantityChange);

  // Handle cart item removal
  document.addEventListener('click', handleCartItemRemoval);

  if (elements.addToCartForm) {
    elements.addToCartForm.addEventListener('submit', addToCart);
  }

  if (elements.sizeSelect) {
    elements.sizeSelect.addEventListener('change', () => {
      resetAddToCartButton(elements.addToCartButton);
      updateProductImageBasedOnSelection();
    });
  }

  if (elements.colorOptions) {
    elements.colorOptions.addEventListener('click', (event) => {
      if (event.target.classList.contains('color-option')) {
        selectColor(event.target);
      }
    });
  }
};

// Initialize quantity button states for existing cart items
const initializeQuantityButtonStates = () => {
  const quantityWrappers = document.querySelectorAll('.cart-item-quantity-wrapper');
  quantityWrappers.forEach(wrapper => {
    updateQuantityButtonStates(wrapper);
  });
};

// Initialization
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing cart');
  
  // Debug element initialization
  console.log('Elements found:', {
    cartButton: !!elements.cartButton,
    cartMenu: !!elements.cartMenu,
    overlay: !!elements.overlay
  });
  
  setupEventListeners();
  selectColorFromURL();
  initializeQuantityButtonStates(); // Initialize button states for existing items
  
  // Initialize confirmation dialog
  if (window.ConfirmationDialog) {
    window.ConfirmationDialog.init();
  }
  
  const cartItemsContainer = document.getElementById('cart-items-container');
  if (cartItemsContainer && !cartItemsContainer.querySelector('#cart-items')) {
    updateCartContents();
  }

  // Generate addons on page load if cart has items
  fetchCartData()
    .then(async cart => {
      // Only redraw when the cart moved on since the server rendered it (line keys change on every write).
      const signature = cart.items.map(item => item.key).join(',') + '|' + cart.total_price;
      if (pendingCartWrites === 0 && elements.cartMenu?.dataset.cartSignature !== signature) await updateCart(cart);
      if (cart.items.length > 0 && window.CartAddons) {
        window.CartAddons.generateGroupedAddons(cart.items).catch(error => {
          console.error('Error generating initial grouped add-ons:', error);
        });
      }
      // Preorder message is now auto-initialized, but ensure it's up to date
      if (window.CartPreorderMessage && cart.items.length > 0) {
        try {
          await window.CartPreorderMessage.updateMessage(cart);
        } catch (error) {
          console.error('Error updating preorder message on page load:', error);
        }
      }
    })
    .catch(error => console.error('Error fetching cart data on page load:', error));
});

function updateMenuCart(cart) {
  console.log('📦 Updating menu cart with', cart.items.length, 'items');
  const menuCartItemsContainer = document.getElementById('menu-cart-items-container');
  const menuCartItemTemplate = document.getElementById('menu-cart-item-template');
  const menuCartFooter = document.querySelector('.menu-cart-footer');
  const cartNotesSection = document.querySelector('.cart-notes-section');
  
  if (!menuCartItemsContainer) {
    console.error('Menu cart items container not found');
    return;
  }

  // Clear existing items
  menuCartItemsContainer.innerHTML = '';
  
  if (cart.items.length === 0) {
    // Cart is empty, show the empty cart message
    const menuEmptyTemplate = getTemplateContent('cart-empty-menu-template');
    if (menuEmptyTemplate) {
      menuCartItemsContainer.innerHTML = '';
      menuCartItemsContainer.appendChild(menuEmptyTemplate);
    }
    // Hide the menu cart footer, notes section, and addons section
    if (menuCartFooter) {
      menuCartFooter.style.display = 'none';
    }
    if (cartNotesSection) {
      cartNotesSection.style.display = 'none';
    }
    
    // Hide addons section when cart is empty
    const addonsSection = document.getElementById('cart-addons-section');
    if (addonsSection) {
      addonsSection.style.display = 'none';
    }
  } else {
    // Cart has items, proceed with normal update
    const div = document.createElement('div');
    div.id = 'menu-cart-items';
    div.className = 'cart-items-list';
    
    // Process cart items
    for (const item of cart.items) {
      const cartItem = menuCartItemTemplate.content.cloneNode(true);
      updateCartItemElement(cartItem, item, 'menu-');
      div.appendChild(cartItem);
    }
    
    menuCartItemsContainer.appendChild(div);
    
    // Generate grouped add-ons after all cart items are processed
    if (window.CartAddons) {
      window.CartAddons.generateGroupedAddons(cart.items).catch(error => {
        console.error('Error generating grouped add-ons:', error);
      });
    }
    
    // Update total in footer
    const menuCartTotal = document.getElementById('menu-cart-total');
    if (menuCartTotal) {
      menuCartTotal.textContent = Shopify.formatMoney(cart.total_price);
    }

    // Show the menu cart footer and notes section
    if (menuCartFooter) {
      menuCartFooter.style.display = 'block';
    }
    if (cartNotesSection) {
      cartNotesSection.style.display = 'block';
      
      // Update the cart note textarea with current cart note
      const cartNoteTextarea = cartNotesSection.querySelector('#cart-note-drawer');
      if (cartNoteTextarea) {
        cartNoteTextarea.value = cart.note || '';
      }
    }
  }
}

// The cart page is redrawn by Shopify, not by this file.
//
// Rebuilding the rows here meant recreating in JS what cart.liquid already
// renders — prices, the discount block, the gift row's own markup — and matching
// rows by a line key Shopify reissues on every write. The Section Rendering API
// hands back the section as HTML, already correct, with keys that are current by
// definition.
//
// Only the cart page goes this way for now. The drawer keeps updateMenuCart.
// A plain read. GET ?sections= on any path renders the named sections and
// answers with JSON, so nothing is posted to fetch markup. /cart?sections= was
// measured returning no sections at all; the root path does.
//
// The id goes out exactly as the page carries it, so Shopify renders the
// instance configured in templates/cart.json rather than some other one, and it
// comes back under that same key.
const fetchCartSectionHTML = async (sectionId) => {
  const response = await fetch(`/?sections=${encodeURIComponent(sectionId)}`);
  if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
  const data = await response.json();

  // The markup sits at the top level under the section's own id — measured:
  // {"template--…__cart": "<div id=\"shopify-section-template--…"}. There is no
  // sections wrapper on this endpoint, unlike the cart write endpoints, which do
  // nest it. Reading data.sections gave undefined every time, and the swap was
  // skipped as if the reply had come back empty.
  const html = data[sectionId] ?? data.sections?.[sectionId] ?? null;
  if (!html) {
    console.error('Section reply held no markup for', sectionId, '| keys:', Object.keys(data));
  }
  return html;
};

// Shopify wraps the section in its own <div id="shopify-section-…">. Swapping
// that wrapper in whole would nest one inside the other, so its contents are
// taken and the wrapper the page already has is kept.
const swapCartSection = (html, sectionId) => {
  const host = cartSectionElement();
  if (!host) return false;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const incoming = parsed.getElementById(`shopify-section-${sectionId}`);
  if (!incoming) {
    console.error('Cart section came back under a different id than requested:', sectionId);
    return false;
  }

  host.innerHTML = incoming.innerHTML;

  // Button states are stamped onto the markup, so they come back blank.
  initializeQuantityButtonStates();

  // Nothing is done here about the preorder lock. cart.liquid renders the
  // checkout button already disabled when the cart is blocked, so the state
  // arrives with the markup and needs no repair.
  //
  // Reapplying it from sessionStorage was tried and was worse than doing
  // nothing: updateCheckoutButtons does not merely read that value, it writes
  // it, and passing false clears the key and releases every checkout control in
  // the document — the drawer's included. The value is null until updateMessage
  // has finished analysing, so a swap arriving first handed it a false it had no
  // business acting on.

  return true;
};

const renderCartSection = async () => {
  // A click still waiting to be sent would have its number overwritten by the
  // cart as it stands now. The swap waits; the write that follows brings its own
  // updateMainCart, and if it never arrives the deferred flag is honoured below.
  if (cartPageWritePending()) {
    cartSectionDeferred = true;
    return;
  }

  const sectionId = cartSectionId();
  if (!sectionId) {
    // Not a cart page. Only worth reporting when the rows are there but the
    // section around them is not, which would mean the markup moved.
    if (document.getElementById('cart-items-container')) {
      console.error('Cart rows found, but no enclosing Shopify section — cannot redraw');
    }
    return;
  }

  const token = ++cartSectionRequest;
  try {
    const html = await fetchCartSectionHTML(sectionId);
    // Overtaken while this was in flight: a newer request will paint instead.
    if (token !== cartSectionRequest || !html) return;
    swapCartSection(html, sectionId);
  } catch (error) {
    console.error('Could not render the cart section:', error);
  }
};

function updateMainCart(cart) {
  const cartItemsContainer = document.getElementById('cart-items-container');
  const cartItemTemplate = document.getElementById('cart-item-template');
  const cartTotal = document.getElementById('cart-total');
  const cartForm = document.querySelector('form[action="/cart"]');

  // On the cart page the server draws it. Everywhere else there is nothing here
  // to draw and the rest of this function is skipped anyway.
  if (cartSectionElement()) {
    renderCartSection();
    return;
  }

  if (cartItemsContainer) {
    // Check if the cart is already populated
    if (cartItemsContainer.querySelector('#cart-items') && cart.items.length > 0) {
      // Cart is already populated, just update quantities and totals
      updateExistingCartItems(cart);
      return;
    }

    cartItemsContainer.innerHTML = '';
    
    if (cart.items.length === 0) {
      // Cart is empty
      const mainEmptyTemplate = getTemplateContent('cart-empty-main-template');
      if (mainEmptyTemplate) {
        cartItemsContainer.innerHTML = '';
        cartItemsContainer.appendChild(mainEmptyTemplate);
      }
      
      // Hide the cart total, checkout button, and other elements
      if (cartTotal) cartTotal.closest('.cart-total-container').style.display = 'none';
      const checkoutButton = cartForm.querySelector('button[name="checkout"]');
      if (checkoutButton) checkoutButton.style.display = 'none';
      
      // Hide additional checkout buttons if they exist
      const additionalCheckoutButtons = cartForm.querySelector('.additional-checkout-buttons');
      if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = 'none';
    } else {
      // Cart has items
      const div = document.createElement('div');
      div.id = 'cart-items';
      div.className = 'cart-items-list';
      
      // Process cart items (main cart doesn't need add-ons in most cases)
      cart.items.forEach(item => {
        const cartItem = cartItemTemplate.content.cloneNode(true);
        updateCartItemElement(cartItem, item, 'cart-');
        div.appendChild(cartItem);
      });
      
      cartItemsContainer.appendChild(div);
      
      // Show and update the cart total
      if (cartTotal) {
        cartTotal.textContent = Shopify.formatMoney(cart.total_price);
        cartTotal.closest('.cart-total-container').style.display = '';
      }
      
      // Show the checkout button
      const checkoutButton = cartForm.querySelector('button[name="checkout"]');
      if (checkoutButton) checkoutButton.style.display = '';
      
      // Show additional checkout buttons if they exist
      const additionalCheckoutButtons = cartForm.querySelector('.additional-checkout-buttons');
      if (additionalCheckoutButtons) additionalCheckoutButtons.style.display = '';
    }
  }
}

function updateExistingCartItems(cart) {
  const container = document.getElementById('cart-items-container');
  if (!container) return;

  // Every lookup is scoped to this container, and that is the point. This
  // function used to call updateItemPrices, which does not exist — the
  // ReferenceError threw on the first item and nothing below it ran, so the
  // subtotal kept its old value after a quantity change.
  //
  // Calling the real updateItemPrice would not do either: it finds the row with
  // a document-wide querySelectorAll and would repaint the drawer's copy of the
  // same line, from unitPrice * quantity — the list price. On a discounted item
  // the drawer then read 59,90 beside a subtotal of 56,91. Prices come from the
  // server's line_price here for that reason: it is the only number that
  // already carries the discount.
  cart.items.forEach(item => {
    const cartItem = container.querySelector(`.cart-item[data-key="${item.key}"]`);
    if (!cartItem) return;

    const quantityInput = cartItem.querySelector('.cart-item-quantity-input');
    if (quantityInput) {
      quantityInput.value = item.quantity;
      quantityInput.dataset.currentQuantity = item.quantity;
    }

    setCartItemPrice(cartItem.querySelector('.cart-item-price'), item.line_price, item);

    const wrapper = cartItem.querySelector('.cart-item-quantity-wrapper');
    if (wrapper) {
      updateQuantityButtonStates(wrapper);
    }
  });

  // The gift is the one row nothing else here cleans up. Ordinary items are
  // removed by updateCartItemQuantity, which deletes the row it just took out,
  // but the gift leaves on its own: syncFreeGiftAddon posts to /cart/change.js
  // when the cart drops below the threshold, and the line stayed on the page
  // showing a gift the server had already reclaimed.
  //
  // Matched on the auto-gift class, never on the line key: Shopify reissues the
  // keys of the remaining items on every change, so anything key-based here
  // recognises nothing and would empty the whole list.
  const hasGift = cart.items.some(item => item.properties && item.properties._auto_gift);
  if (!hasGift) {
    container.querySelectorAll('.cart-item--auto-gift').forEach(row => row.remove());
  }

  // Update total
  const cartTotal = document.getElementById('cart-total');
  if (cartTotal) {
    cartTotal.textContent = Shopify.formatMoney(cart.total_price);
  }
}

function updateItemPriceAndTotal(item, newQuantity) {
  const newLinePrice = item.price * newQuantity;
  const cartItem = document.querySelector(`.cart-item[data-key="${item.key}"]`);
  const priceElement = cartItem.querySelector('.cart-item-price');
  setCartItemPrice(priceElement, newLinePrice, item);

  // Update the total price
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const cartTotal = document.getElementById('cart-total');
      if (cartTotal) {
        cartTotal.textContent = Shopify.formatMoney(cart.total_price);
      }
    })
    .catch(error => console.error('Error updating total price:', error));
}

function updateCartCount(count) {
  const cartCountElement = document.querySelector('#cart-button .cart-count');
  
  if (cartCountElement) {
    cartCountElement.textContent = count;
    if (count > 0) {
      cartCountElement.classList.remove('hidden');
    } else {
      cartCountElement.classList.add('hidden');
    }
  }
}

// Updated function to update URL
function updateURL(color) {
  if (history.pushState) {
    const formattedColor = color.toLowerCase().replace(/\s+/g, '-');
    const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?color=' + encodeURIComponent(formattedColor);
    window.history.pushState({path:newurl},'',newurl);
  }
}

// Updated function to select color from URL parameter
function selectColorFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const color = urlParams.get('color');
  if (color) {
    const formattedColor = color.replace(/-/g, ' ');
    const colorButton = document.querySelector(`.color-option[data-color="${formattedColor}"]`);
    if (colorButton) {
      selectColor(colorButton);
    }
  }
}

function updateAvailableSizes(color) {
  const sizeSelect = document.getElementById('size-select');
  const variants = getProductVariants();
  
  if (!sizeSelect || variants.length === 0) return;
  
  // Store the current selection
  const currentSelection = sizeSelect.value;

  // Get size options for the selected color
  const availableSizes = new Set();
  const unavailableSizes = new Set();
  
  variants.forEach(variant => {
    if (variant.options.includes(color)) {
      // Find the size option (the option that isn't the color)
      const sizeIndex = variant.option_names.findIndex(name => 
        name !== "Color" && name !== "Farbe" && name !== "Colour"
      );
      if (sizeIndex !== -1) {
        const size = variant.options[sizeIndex];
        if (variant.available) {
          availableSizes.add(size);
        } else {
          unavailableSizes.add(size);
        }
      }
    }
  });

  // Update the size select using the global function
  if (window.populateSizeSelect) {
    window.populateSizeSelect(availableSizes, unavailableSizes);
  }

  // Restore the previous selection if it's still available
  if (Array.from(sizeSelect.options).some(option => option.value === currentSelection && !option.disabled)) {
    sizeSelect.value = currentSelection;
  } else {
    sizeSelect.value = ""; // Reset to default option if previous selection is not available
  }

  // Find the first available variant with this color and update the image
  const selectedVariant = variants.find(variant => variant.options.includes(color) && variant.available);
  if (selectedVariant) {
    updateProductImage(selectedVariant);
  }
}

// New function to set error state on Add to Cart button
function setAddToCartButtonError(button) {
  button.textContent = getLocaleString('product.please_select');
  button.style.backgroundColor = 'red';
  button.style.color = 'white';
  button.disabled = true;
}

// New function to reset Add to Cart button
function resetAddToCartButton(button) {
  button.textContent = button.dataset.purchaseLabel || getLocaleString('cart.add_to_cart');
  button.style.backgroundColor = '';
  button.style.color = '';
  button.disabled = false;
}



Shopify.formatMoney = function(cents, format) {
  format = format || Shopify.money_format || "${{amount}}";
  var value = (cents / 100).toFixed(2);
  return format.replace("{{amount}}", value).replace("{{amount_with_comma_separator}}", value.replace('.', ','));
};

function findVariantImageIndex(variant) {
  const variantImage = variant.featured_image;
  if (!variantImage) {
    return -1;
  }

  const imagesElement = document.getElementById('product-images');
  if (!imagesElement) {
    return -1;
  }

  const images = JSON.parse(imagesElement.textContent);


  // Try to find an exact match
  let index = images.findIndex(image => image === variantImage.src);
  
  // If no exact match, try to find a partial match
  if (index === -1) {
    index = images.findIndex(image => variantImage.src.includes(image) || image.includes(variantImage.src));
  }

  return index;
}

function updateProductImage(variant) {
  const imageIndex = findVariantImageIndex(variant);
  if (window.productSplide && imageIndex !== -1) {
    window.productSplide.go(imageIndex);
    lastValidImageIndex = imageIndex;
  } else {
    console.log('No matching image found or Splide instance not available. Keeping current slide.');
    if (window.productSplide) {
      window.productSplide.go(lastValidImageIndex);
    }
  }
}

console.log('Cart JavaScript loaded');

// Header scroll behavior for index template
document.addEventListener('DOMContentLoaded', function() {
  const header = document.getElementById('site-header');
  const isIndexPage = document.body.classList.contains('template-index');
  
  if (header && isIndexPage) {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > 50) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initial state
    handleScroll();
  }
});


// Function to update cart note
function updateCartNote(note) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ note: note })
  })
  .then(response => response.json())
  .then(cart => {
    console.log('Cart note updated:', cart.note);
  })
  .catch(error => console.error('Error updating cart note:', error));
}

// Listen for variant changes and update price display
document.addEventListener('variantChanged', function(event) {
  const { variant, formId } = event.detail;
  
  // Only update price for main product page
  if (formId !== 'main-product' || !variant) return;
  
  // Find the price container in product-meta
  const priceContainer = document.querySelector('.product-meta-price');
  if (!priceContainer) return;
  
  // Format prices
  const price = Shopify.formatMoney(variant.price);
  const compareAtPrice = variant.compare_at_price ? Shopify.formatMoney(variant.compare_at_price) : null;
  
  // Build new price HTML
  let priceHTML = '';
  if (compareAtPrice && variant.compare_at_price > variant.price) {
    priceHTML = `
      <span class="line-through opacity-50">${compareAtPrice}</span>
      <span>${price}</span>
    `;
  } else {
    priceHTML = `<span>${price}</span>`;
  }
  
  // Update the price display
  priceContainer.innerHTML = priceHTML;
});

// Make cart functions globally accessible
window.updateCart = updateCart;
window.updateCartContents = updateCartContents;
window.updateMenuCart = updateMenuCart;
window.updateMainCart = updateMainCart;
window.updateCartCount = updateCartCount;
window.updateCartNote = updateCartNote;
window.toggleCartDrawer = () => toggleMenu(elements.cartMenu, true);
window.__toggleCountryDrawer = (open) => toggleMenu(elements.countryDrawer, open);
window.closeAllMenus = closeAllMenus;
window.addEventListener('pageshow', event => {
  if (event.persisted) {
    cartWriteChain.then(() => updateCartContents()).catch(error => {
      console.error('Error restoring cart:', error);
    });
  }
});
